import { useMutation as useApolloMutation } from '@apollo/client';
import { useCallback, useRef } from 'react';
import { useEventsCorrelation } from './useEventsCorrelation';
import { v4 } from 'uuid';
import { useParams } from 'react-router-dom';
import { useServerErrorHandling } from 'hooks';

const CORRELATION_ID = 'x-correlation-id';
const DOCUMENT_ID = 'document-id';

const setHeader = (options, key, value) => {
  const headers = options.context?.headers;
  if (headers) {
    options.context.headers = { ...headers, [key]: value };
  } else {
    options.context = { ...options.context, headers: { [key]: value } };
  }
};

const setCorrelationOnRequest = options => {
  const correlationId = v4();
  setHeader(options, CORRELATION_ID, correlationId);
  return correlationId;
};

const useDocumentHeaders = options => {
  const params = useParams();
  if (params.id) {
    setHeader(options, DOCUMENT_ID, params.id);
  }
};

export const useMutation = (scheme, options) => {
  const correlationId = useRef(null);
  const { persistCorrelation } = useEventsCorrelation();

  // extract custom options
  const { setCorrelation, onCorrelationUsed, serverErrorHandlingOptions = {}, ...restOptions } = options;

  const { storeKey } = useServerErrorHandling({
    effect: serverErrorHandlingOptions.effect || restOptions.onError || (_ => {}),
    ...serverErrorHandlingOptions
  });

  //custom headers
  useDocumentHeaders(restOptions);
  if (setCorrelation) {
    correlationId.current = setCorrelationOnRequest(restOptions);
  }

  const [mutationInternal, data] = useApolloMutation(scheme, restOptions);

  const mutation = useCallback(
    async input => {
      if (!input) return;

      //extract custom inputs
      const { mutationKey, ...restInput } = input;
      correlationId.current && onCorrelationUsed && onCorrelationUsed(correlationId.current);
      let response = mutationInternal(restInput);

      if (!correlationId.current) {
        // if correlationId is not already set, await the mutation promise and extract the correlation from its response.
        response = await response; // in this point response is a promise
        if (!response) {
          console.error(`Mutation '${scheme.definitions[0].name.value}' response is undefined.`);
          return;
        }

        if (response.data) {
          const { correlationId: correlationIdFromResponse } = response.data[Object.keys(response.data)[0]];
          correlationId.current = correlationIdFromResponse;
        } else correlationId.current = null;
      }

      storeKey({ correlationId: correlationId.current });

      /*mutation key is used to make a difference between same mutations but with different contexts executed in parallel.*/
      const mutationName = scheme.definitions[0].name.value;
      await persistCorrelation({ name: mutationName, key: mutationKey, correlationId: correlationId.current });
      //in future if more definitions will be used iterate through them and call setCorrelation for each one.

      //reset correlation id ref
      correlationId.current = null;

      return response;
    },
    [mutationInternal, persistCorrelation, scheme, storeKey, onCorrelationUsed]
  );

  return [mutation, data];
};
